import { FC, useState } from 'react';

import { isEmpty } from 'lodash';
import { RiInformationLine } from 'react-icons/ri';

import { HEALTH_ADDON_ILLNESS_LISTS } from '@healthquote/constants';
import {
  HealthAddOn,
  HealthAddOnSubOptionValue,
  useHealthQuoteStore
} from '@store/healthquote';
import { Box, Drawer, Flex, Grid, List, Radio, Select, Text } from '@ui';

interface AddOnSubOptionsProps {
  addOn: HealthAddOn;
  namePrefix?: string;
  /** List to show in the drawer, driven externally (e.g. first-include from parent) */
  illnessListTrigger?: string[] | null;
  /** Called when the drawer is closed so the parent can clear the trigger */
  onIllnessListClose?: () => void;
}

const AddOnSubOptions: FC<AddOnSubOptionsProps> = ({
  addOn,
  namePrefix = '',
  illnessListTrigger,
  onIllnessListClose
}) => {
  const { toggleAddOn, loadingPremium, addOnSelectionWarning } = useHealthQuoteStore(
    (state) => ({
      toggleAddOn: state.toggleAddOn,
      loadingPremium: state.loadingPremium,
      addOnSelectionWarning: state.addOnSelectionWarning
    })
  );

  // Addons like rped / sped_mod expose an illness list on their sub options
  const illnessListConfig = HEALTH_ADDON_ILLNESS_LISTS[addOn.key];

  const getIllnessList = (subOptVal: HealthAddOnSubOptionValue) =>
    illnessListConfig ? subOptVal[illnessListConfig.listKey] : undefined;

  const [localList, setLocalList] = useState<string[] | null>(null);

  // Derive open state and content from either a local interaction or the parent trigger.
  const isDrawerOpen =
    !addOnSelectionWarning && (!!localList || !!illnessListTrigger?.length);
  const currentList = localList ?? illnessListTrigger ?? null;

  const openIllnessDrawer = (list: string[]) => setLocalList(list);

  const closeIllnessDrawer = () => {
    setLocalList(null);
    onIllnessListClose?.();
  };

  const handleSubOptionChange = (
    subOptionKey: string,
    subOptVal: HealthAddOnSubOptionValue
  ) => {
    toggleAddOn(addOn.key, addOn.included, {
      subOptionKey,
      value: subOptVal
    });
    const illnessList = getIllnessList(subOptVal);
    if (illnessList?.length) {
      openIllnessDrawer(illnessList);
    }
  };

  const subOptionsContent =
    addOn && addOn.subOptions && addOn.subOptions.length
      ? addOn.subOptions.map((subOption) => {
          const optionType = subOption.type;
          // Only 'radio' and 'select' types are supported for now
          if (optionType !== 'radio' && optionType !== 'select') return null;

          return (
            <Grid align="center" key={subOption.key}>
              <Grid.Col span={12}>
                <Flex
                  direction={{ sm: 'column', md: 'row' }}
                  align="flex-start"
                  gap="sm"
                  px="sm"
                  py="xs"
                  wrap="wrap"
                >
                  {subOption.type === 'radio' ? (
                    subOption.values.map((subOptVal, index) => {
                      const illnessList = getIllnessList(subOptVal);
                      return (
                        <Radio
                          key={`${subOption.key}-opt-${index}`}
                          styles={{
                            radio: {
                              padding: 2,
                              color:
                                addOn.subOptionValues &&
                                addOn.subOptionValues[subOption.key].value ===
                                  subOptVal.value
                                  ? '#228be6' // Primary color
                                  : '#adb5bd' // Grey color
                            }
                          }}
                          checked={
                            addOn.subOptionValues &&
                            addOn.subOptionValues[subOption.key].value ===
                              subOptVal.value
                          }
                          value={subOptVal.value}
                          onChange={() =>
                            handleSubOptionChange(subOption.key, subOptVal)
                          }
                          size="md"
                          name={`${namePrefix}${subOption.key}`}
                          disabled={loadingPremium}
                          label={
                            illnessList?.length ? (
                              <Flex align="center" gap={4}>
                                <span>{subOptVal.label}</span>
                                <RiInformationLine
                                  size={14}
                                  style={{ cursor: 'pointer', flexShrink: 0 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openIllnessDrawer(illnessList);
                                  }}
                                />
                              </Flex>
                            ) : (
                              subOptVal.label
                            )
                          }
                        />
                      );
                    })
                  ) : subOption.type === 'select' ? (
                    <Box w="90%">
                      <Select
                        value={
                          addOn.subOptionValues
                            ? addOn.subOptionValues[subOption.key].value
                            : ''
                        }
                        onChange={(value) => {
                          const selectedVal = subOption.values.find(
                            (item) => item.value === value
                          )!;
                          handleSubOptionChange(subOption.key, selectedVal);
                        }}
                        data={subOption.values.map((opt) => ({
                          value: opt.value,
                          label: opt.label
                        }))}
                        name={`${namePrefix}${subOption.key}`}
                        placeholder={'Select'}
                        size="xs"
                      />
                    </Box>
                  ) : null}
                </Flex>
              </Grid.Col>
            </Grid>
          );
        })
      : addOn?.subOptionValues
      ? !addOn?.noDefaultOption &&
        !isEmpty(Object.keys(addOn.subOptionValues)) &&
        Object.keys(addOn.subOptionValues).map((subOptKey) => {
          return (
            <Grid key={subOptKey} align="center">
              <Grid.Col span={{ md: 8, sm: 8 }}>
                <Flex align="center">
                  <Text c="dimmed" size="sm" style={{ whiteSpace: 'nowrap' }}>
                    {addOn.subOptionValues?.[subOptKey].label}
                  </Text>
                </Flex>
              </Grid.Col>
            </Grid>
          );
        })
      : null;

  return (
    <>
      {subOptionsContent}
      {illnessListConfig && (
        <Drawer
          opened={isDrawerOpen}
          onClose={closeIllnessDrawer}
          position="right"
          headerTitle={illnessListConfig.headerTitle}
          size="md"
        >
          <Box p="md">
            <Text size="md" mb={20} display="inline-block" fw={600}>
              {illnessListConfig.note}
            </Text>
            <List size="sm" spacing="xs">
              {currentList?.map((disease, index) => (
                <List.Item key={index}>{disease}</List.Item>
              ))}
            </List>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export { AddOnSubOptions };
